import { useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  LOGO_ALLOWED_MIME_TYPES,
  LOGO_MAX_BYTES,
  LOGO_MAX_DIMENSION,
  LOGO_MIN_DIMENSION,
} from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { useDeleteLogo, useUploadLogo } from '../hooks/useBranding.js'
import { LogoCropModal } from '../branding/LogoCropModal.js'
import { logoSrc } from '../logoSrc.js'

/**
 * Logo upload control — current logo preview (or the Naya default with a
 * "using Naya default" label), a mobile-friendly file picker, and a
 * "Revert to default" button when a custom logo is set.
 *
 * Picking a file opens `LogoCropModal` (drag-to-square + zoom). Confirm in the
 * modal produces a 600×600 PNG Blob and uploads it straight away — there's no
 * separate "Upload" button anymore, the crop confirm *is* the upload.
 *
 * Cheap client-side checks (size + format) run before the modal opens so the
 * obvious rejections are instant; the server is still authoritative for
 * dimensions and magic-byte sniffing. Server errors surface inline.
 *
 * `onUploaded` lets the setup wizard advance after a successful upload.
 */
export function LogoUploader({
  currentLogoUrl,
  defaultLogoUrl,
  logoUpdatedAt,
  onUploaded,
}: {
  /** The tenant's custom logo URL, or null when using the Naya default. */
  currentLogoUrl: string | null
  /** The Naya default logo URL — shown as the preview when there's no custom logo. */
  defaultLogoUrl: string
  /**
   * Tenant `logo_updated_at` (ISO). Cache-bust key for the preview — the logo
   * storage key is overwrite-no-versioning, so without this a re-upload shows
   * the stale image until a hard reload. See `logoSrc`.
   */
  logoUpdatedAt: string
  onUploaded?: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  // The source file currently being cropped. Non-null ⇒ the crop modal is open.
  const [cropSource, setCropSource] = useState<File | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)

  const upload = useUploadLogo()
  const del = useDeleteLogo()

  function pickFile(file: File | null) {
    setClientError(null)
    // Reset the input so re-picking the *same* file still fires onChange.
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    // Fast client-side gates — the server re-validates everything.
    if (!LOGO_ALLOWED_MIME_TYPES.includes(file.type as never)) {
      setClientError('Logo must be a PNG or JPEG image.')
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      setClientError(
        `Logo file is too large. The maximum is ${LOGO_MAX_BYTES / (1024 * 1024)}MB.`,
      )
      return
    }
    setCropSource(file)
  }

  function handleConfirm(blob: Blob) {
    upload.mutate(blob, {
      onSuccess: () => {
        toast.success('Logo updated')
        setCropSource(null)
        onUploaded?.()
      },
      onError: (err) => {
        // Server validation (dimensions, magic bytes) → inline message. Close
        // the modal so the owner can re-pick; the error shows on the page.
        setCropSource(null)
        setClientError(
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Upload failed. Please try again.',
        )
      },
    })
  }

  function doRevert() {
    del.mutate(undefined, {
      onSuccess: () => toast.success('Reverted to the Naya default logo'),
      onError: (err) =>
        setClientError(
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Could not revert. Please try again.',
        ),
    })
  }

  const hasCustomLogo = !!currentLogoUrl
  // Preview priority: the tenant's custom logo → the Naya default. There's
  // always something to show — no empty grey box. The custom logo carries a
  // `?v=` cache-bust param so a fresh upload repaints without a reload.
  const displayUrl = logoSrc(currentLogoUrl, defaultLogoUrl, logoUpdatedAt)

  return (
    <div>
      <div className="owner-logo-row">
        <div>
          <img
            className="owner-logo-preview"
            src={displayUrl}
            alt={hasCustomLogo ? 'Logo preview' : 'Naya default logo'}
          />
          {!hasCustomLogo && (
            <p className="owner-logo-default-label">Using the Naya default</p>
          )}
        </div>

        <div className="owner-logo-actions">
          <input
            ref={inputRef}
            type="file"
            className="owner-file-input"
            accept="image/png,image/jpeg"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="owner-btn"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? 'Uploading…' : 'Choose an image'}
          </button>

          {hasCustomLogo && (
            <button
              type="button"
              className="owner-btn owner-btn-danger"
              onClick={doRevert}
              disabled={del.isPending}
            >
              {del.isPending ? 'Reverting…' : 'Revert to default'}
            </button>
          )}

          <p className="owner-hint">
            PNG or JPEG, square, {LOGO_MIN_DIMENSION}–{LOGO_MAX_DIMENSION}px,
            max {LOGO_MAX_BYTES / (1024 * 1024)}MB. You'll crop it to a square next.
          </p>
        </div>
      </div>

      {clientError && (
        <p className="owner-field-error" role="alert">
          {clientError}
        </p>
      )}

      {cropSource && (
        <LogoCropModal
          file={cropSource}
          isUploading={upload.isPending}
          onConfirm={handleConfirm}
          onCancel={() => setCropSource(null)}
          cropShape="round"
        />
      )}
    </div>
  )
}
